import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MagnifyingGlass, Plus } from '@phosphor-icons/react';
import AsyncState from '../../components/AsyncState';
import LifecycleShell from '../../components/LifecycleShell';
import StatusBadge from '../../components/StatusBadge';
import StatusStepper from '../../components/StatusStepper';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useApi } from '../../hooks/useApi';
import { translations } from '../../translations';
import { fillTemplate } from '../../utils/format';

const POLL_MS = 30000;
const PAGE_SIZE = 20;

const FILTERS = [
  { key: 'all', label: 'filterAll', params: {} },
  { key: 'new', label: 'filterNew', params: { status: 'request_submitted' } },
  { key: 'progress', label: 'filterInProgress', params: { view: 'in_progress' } },
  { key: 'google', label: 'filterGoogleReview', params: { status: 'google_play_review' } },
  { key: 'published', label: 'filterPublished', params: { status: 'published' } },
];

const isActive = (filter, params) => {
  const wanted = Object.entries(filter.params);
  if (wanted.length === 0) return !params.get('status') && !params.get('view');
  return wanted.every(([key, value]) => params.get(key) === value);
};

export default function CustomAppsListPage() {
  const { language } = useLanguage();
  const t = translations[language];
  const { can } = useCurrentUser();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('q') || '');
  const page = Math.max(1, Number(params.get('page')) || 1);

  const apiParams = new URLSearchParams({ page: String(page), size: String(PAGE_SIZE) });
  ['status', 'view', 'q', 'crm_company_id'].forEach((key) => { if (params.get(key)) apiParams.set(key, params.get(key)); });
  const list = useApi(`/custom-apps?${apiParams}`, { pollMs: POLL_MS });
  const items = list.data?.items || [];
  const totalPages = Math.max(1, Math.ceil((list.data?.total_items || 0) / PAGE_SIZE));
  const companyId = params.get('crm_company_id');
  const companyName = items[0]?.company?.name || companyId;

  const update = (changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([key, value]) => (value ? next.set(key, value) : next.delete(key)));
    if (!('page' in changes)) next.delete('page');
    setParams(next);
  };
  const chooseFilter = (filter) => update({ status: filter.params.status, view: filter.params.view });
  const submitSearch = (event) => {
    event.preventDefault();
    update({ q: search.trim() });
  };

  return <LifecycleShell
    title={t.listTitle}
    action={can('custom_app:create') ? <Link className="btn btn--primary btn--pill" to="/custom-apps/new"><Plus size={18} aria-hidden="true" /> {t.startRequest}</Link> : null}
  >
    <div className="toolbar">
      <div className="segmented" role="group" aria-label={t.listTitle}>
        {FILTERS.map((filter) => <button
          key={filter.key}
          type="button"
          className="segmented__item"
          aria-pressed={isActive(filter, params)}
          onClick={() => chooseFilter(filter)}
        >{t[filter.label]}</button>)}
      </div>
      <form className="search" onSubmit={submitSearch} role="search">
        <MagnifyingGlass size={20} aria-hidden="true" />
        <input className="input" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t.searchApps} aria-label={t.searchApps} />
        <button className="btn" type="submit">{t.searchAction}</button>
      </form>
    </div>
    {companyId && <p className="notice">
      <span>{fillTemplate(t.filterCompany, { name: companyName })}</span>
      <button className="btn btn--pill" type="button" onClick={() => update({ crm_company_id: '' })}>{t.clearCompanyFilter}</button>
    </p>}
    <section className="card" aria-live="polite" aria-label={t.listTitle}>
      <AsyncState
        state={list}
        retry={list.retry}
        isEmpty={items.length === 0}
        empty={<div className="empty"><strong>{t.noAppsTitle}</strong><span>{t.noAppsHint}</span></div>}
      >
        <div className="row-list">
          {items.map((app) => <Link className="row" to={`/custom-apps/${app.id}`} key={app.id}>
            <span className="row__main">
              <span className="row__title">{app.appName || t.untitledApp}</span>
              <span className="row__meta">{app.company?.name}</span>
              {(app.attentionReasons || []).length > 0 && <span className="row__meta row__meta--warn">{t.attentionBadge}: {app.attentionReasons.map((reason) => t.reasonLabel[reason] || reason).join(', ')}</span>}
            </span>
            <span className="row__side">
              <StatusStepper progress={app.progress} compact />
              <StatusBadge status={app.status} />
            </span>
          </Link>)}
        </div>
      </AsyncState>
    </section>
    {totalPages > 1 && <nav className="pager" aria-label={t.listTitle}>
      <span>{fillTemplate(t.pageOf, { page, total: totalPages })}</span>
      <div className="btn-row">
        <button className="btn" type="button" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>{t.previousPage}</button>
        <button className="btn" type="button" disabled={page >= totalPages} onClick={() => update({ page: String(page + 1) })}>{t.nextPage}</button>
      </div>
    </nav>}
  </LifecycleShell>;
}
